import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import AppRoutes from './Routes/AppRoutes';
import { AppErrorBoundary } from '@views/ErrorBoundary';
import { loadLocalStore } from '@support/localStore';
import { fetchStarred } from '@actions/starred';
import {
  applyUpdatedTheme,
  DarkThemePreference,
  DEFAULT_DARK_THEME_PREFERENCE,
  DEFAULT_THEME_PREFERENCE,
  initializeTheme,
  setStoredDarkThemePreference,
  setStoredThemePreference,
  ThemeContext,
  ThemePreference,
} from './themes';
import Confirm from '@views/Confirm';
import DragAndDropProvider from '@components/DragAndDrop/Context';
import OfflineOverlay from '@views/OfflineOverlay';
import { fetchGroups } from '@actions/groups';
import { useAuthContext } from '@hooks/use-auth-context';
import { fetchEnabledNotifications, fetchUserMetadata } from '@actions/user';
import { fetchAllIntegrations } from '@actions/integrations';
import { getInitialDesktopIntegrationAuthStatuses } from '@actions/deviceIntegrations';
import { fetchSearchTips } from '@actions/search';
// import { backfillExistingEmailClientPreference } from '@support/preferred-email-client';

// Functions to run to backfill new local storage keys or migrate
// existing keyed data. Any function included here should be designed
// to only perform the necessary manipulations a single time.
// const localDataMigrations: (() => void)[] = [backfillExistingEmailClientPreference];

export default function App() {
  const dispatch = useDispatch();
  const { user, isReady } = useAuthContext();
  const [themePreference, _setThemePreference] =
    useState<ThemePreference>(DEFAULT_THEME_PREFERENCE);
  const [darkThemePreference, _setDarkThemePreference] = useState<DarkThemePreference>(
    DEFAULT_DARK_THEME_PREFERENCE,
  );

  const setThemePreference = (pref: ThemePreference) => {
    _setThemePreference(pref);
    setStoredThemePreference(String(user?.id), pref);
    applyUpdatedTheme(pref, darkThemePreference);
  };

  const setDarkThemePreference = (pref: DarkThemePreference) => {
    _setDarkThemePreference(pref);
    setStoredDarkThemePreference(String(user?.id), pref);
    applyUpdatedTheme(themePreference, pref);
  };

  useEffect(() => {
    if (user) {
      if (window.desktopIntegrations) {
        getInitialDesktopIntegrationAuthStatuses(dispatch);
      }

      dispatch(fetchStarred());
      dispatch(fetchGroups());
      dispatch(fetchEnabledNotifications());
      dispatch(fetchUserMetadata());
      dispatch(fetchAllIntegrations({ isFirstLoad: true }));
      dispatch(fetchSearchTips());
    }
  }, [isReady]);

  useEffect(() => {
    if (isReady) {
      const [themePref, darkThemePref] = initializeTheme(user?.id || '');
      _setThemePreference(themePref);
      _setDarkThemePreference(darkThemePref);
      dispatch(loadLocalStore());
      // localDataMigrations.forEach((performMigration) => performMigration());
    }
  }, [isReady]);

  useEffect(() => {
    let timeout = null;
    const handleFocus = (e: FocusEvent) => {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        window.app.emit('window:focus', e);
      }, 1000);
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // TODO something more elegant while waiting for first user data pull
  if (!isReady) {
    return <></>;
  }

  return (
    <ThemeContext.Provider
      value={{ themePreference, setThemePreference, darkThemePreference, setDarkThemePreference }}
    >
      <Helmet encodeSpecialCharacters={true} titleTemplate="Clay - %s" defaultTitle="Clay">
        <html lang="en" />
        <meta name="viewport" content="width=device-width" />
      </Helmet>
      <AppErrorBoundary>
        <DragAndDropProvider>
          <AppRoutes />
          <Confirm />
        </DragAndDropProvider>
      </AppErrorBoundary>
      <OfflineOverlay />
    </ThemeContext.Provider>
  );
}
